import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import authApi, { JWTAccessToken } from '~/authApi';
import clientApi from '~/clientApi';
import { Creator } from '~/clientApi/creator.model';
import { AppLoader } from '~/components/AppLoader';
import { FeatureFlags } from '~/components/FeatureFlagControls';
import { queryClient } from '~/config/reactQueryConfig';
import { LOCALSTORAGE_KEY_LOGOUT, USER_ROLES } from '~/constants';
import { IntercomContext } from '~/contexts/Intercom/IntercomContext';
import { routes } from '~/core/router';
import { generatePath } from '~/core/router/router.helpers';
import { LocalStorageService } from '~/core/utils/storage';
import { getMainPlatformStatus } from '~/creatorUtils';
import { fetchCreator } from '~/ducks/creator';
import { fetchTalentManager } from '~/ducks/talent-manager';
import { usePersistedState } from '~/hooks/usePersistedState';
import {
  CREATOR_ENTITY_QUERY_KEY,
  MANAGER_ENTITY_QUERY_KEY,
} from '~/queries/keys';
import { selectCreator } from '~/selectors/creator';
import { selectTalentManager } from '~/selectors/talent-manager';
import { getUrlSearchParams, urlSearchParamsIncludes } from '~/utils-ts';

import { SKIP_RECONNECT_SESSION_KEY, STATUS } from './auth.constants';
import { mapURLSearchParamsToParams } from './auth.context.mapper';
import { hasTokensInUrl } from './auth.context.utils';
import {
  AuthTokens,
  ImpersonatePayload,
  LinkFacebookAccountPayload,
} from './auth.vm';

interface AuthContextInterface {
  acceptManagerInvite: (invitationId: string) => Promise<void>;
  getMainUrl: (settings?: { skipReconnect?: boolean }) => string;
  impersonate: (payload: ImpersonatePayload) => Promise<void>;
  logout: (params?: { redirectToSSOLogout?: boolean }) => Promise<void>;
  linkFacebookAccount: (
    payload: LinkFacebookAccountPayload,
    redirect: boolean,
  ) => Promise<void>;

  redirect: () => void;
  setSkipReconnect: (value: boolean) => void;
  updateManagedCreatorRoleToCreator: (userId: string) => Promise<void>;
  skipReconnect: boolean;
}

const AuthContext = React.createContext<AuthContextInterface | null>(null);

export interface IAuthProviderProps {
  children?: React.ReactNode;
  skipLoading?: boolean;
}

export const AuthProvider: React.FC<IAuthProviderProps> = ({
  skipLoading = false,
  ...props
}) => {
  const { shutdown } = React.useContext(IntercomContext);
  const [authStatus, setAuthStatus] = React.useState(
    skipLoading ? STATUS.ready : STATUS.loading,
  );

  const [skipReconnect, setSkipReconnect] = usePersistedState(
    SKIP_RECONNECT_SESSION_KEY,
    false,
    typeof window === 'undefined' ? undefined : window.sessionStorage,
  );
  const creator = useSelector(selectCreator);
  const manager = useSelector(selectTalentManager);
  const dispatch = useDispatch();
  const router = useRouter();
  const { hasConnected, hasExpired, hasReAuth } = getMainPlatformStatus(
    creator as Creator,
  );

  /** Add a listener to storage, so when logging out on one tab, all tabs log out*/
  React.useEffect(() => {
    const syncLogout = (event: StorageEvent) => {
      if (event.key === 'logout') {
        window.location.assign(routes.loginSSO);
      }
    };

    window.addEventListener('storage', syncLogout);
    return () => {
      window.removeEventListener('storage', syncLogout);
      LocalStorageService.removeItem(LOCALSTORAGE_KEY_LOGOUT);
    };
  }, [router]);

  const acceptManagerInvite = async (invitationId: string) => {
    const { roles } = authApi.user;

    let jwtAccessToken: JWTAccessToken | undefined;

    /**
     * If the user has creator role we update it to
     * managed-creator, in case he already is we
     * will accept the invite with current user accessToken
     */
    if (roles.includes(USER_ROLES.creator)) {
      const updatedRole = [...authApi.user.roles, USER_ROLES.managed].filter(
        (role) => role !== USER_ROLES.creator,
      );

      setAuthStatus(STATUS.loading);

      // Store new jwt for next request.
      if (creator?.authId) {
        jwtAccessToken = await authApi.updateUserRole(
          {
            addRole: updatedRole,
            removeRole: [USER_ROLES.creator],
            userId: creator.authId,
          },
          false,
        );
      }
    }

    // Accept invite with either the accessToken or sharedToken in case we updated the role.
    const response = await clientApi.invitations.accept({
      id: invitationId,
      _sharedToken: jwtAccessToken?.accessToken,
    });

    if (jwtAccessToken) {
      // Update the jwtAccessToken in authApi and force redirect
      authApi.jwtAccessToken = jwtAccessToken;
    }

    return response;
  };

  const getMainUrl = React.useCallback(
    (settings?: { skipReconnect?: boolean }) => {
      const shouldSkipReconnect = settings?.skipReconnect ?? skipReconnect;

      if (
        authApi.user.roles.includes(USER_ROLES.managed) &&
        !creator?.platformFullAccess
      ) {
        return routes.connectionDashboard;
      }

      if (
        authApi.user.roles.includes(USER_ROLES.creator) ||
        authApi.user.roles.includes(USER_ROLES.managed)
      ) {
        if (!hasConnected) {
          return routes.signUpConnectSocials;
        }

        const shouldReconnect = hasExpired || hasReAuth;

        if (!shouldSkipReconnect && shouldReconnect) {
          return routes.reconnectPlatforms;
        }

        if (creator?.slug && creator?.manager?.id) {
          return routes.platformConnections;
        }

        return routes.mediaKits; // Since media packs are already deprecated
      }

      if (authApi.user.roles.includes(USER_ROLES.talentManager)) {
        if (manager?.agency?.enabled) {
          return routes.myRoster;
        }
        return routes.myRoster;
      }

      return routes.logout;
    },
    [skipReconnect, creator, hasConnected, hasExpired, hasReAuth, manager],
  );

  const fetchUserEntity = async () => {
    // if user is logged in...
    const { id, roles } = authApi.user;
    if (
      roles.includes(USER_ROLES.creator) ||
      roles.includes(USER_ROLES.managed)
    ) {
      // ...fetch corresponding creator...
      const fetchedCreator = await queryClient.fetchQuery({
        queryKey: [CREATOR_ENTITY_QUERY_KEY, id],
        queryFn: () => clientApi.creators.read(id),
      });
      dispatch((fetchCreator as any).fulfilled(fetchedCreator));
    } else if (roles.includes(USER_ROLES.talentManager)) {
      // ...fetch corresponding talentManager...
      const fetchedTalentManager = await queryClient.fetchQuery({
        queryKey: [MANAGER_ENTITY_QUERY_KEY, id],
        queryFn: () =>
          clientApi.managers.read({
            id,
            _isImpersonatingDisabled: true,
            properties: [
              'agency',
              'agencyName',
              'createdAt',
              'createdAtAgo',
              'email',
              'enabledControls',
              'id',
              'isPasswordProtected',
              'lastActivity',
              'lastLogin',
              'name',
              'registeredAt',
              'setting',
              'sharedViews',
              'shareToken',
              'updatedAt',
              'updatedAtAgo',
              'watchlistId',
            ],
          }),
      });
      dispatch((fetchTalentManager as any).fulfilled(fetchedTalentManager));
    }
  };

  const logout = async (
    { redirectToSSOLogout } = { redirectToSSOLogout: true },
  ) => {
    setAuthStatus(STATUS.loading);

    queryClient.clear();
    if (window.heap) {
      try {
        window.heap.resetIdentity();
      } catch (error) {
        datadogRum.addError(new Error('Heap resetIdentity failed'), {
          error,
        });
      }
    }
    shutdown();
    datadogRum.clearUser();
    await authApi.logout({ redirectToSSOLogout });
  };

  const redirect = async () => {
    setAuthStatus(STATUS.redirecting);
  };

  const impersonate = async (payload: ImpersonatePayload) => {
    await authApi.impersonate(payload);
    setAuthStatus(STATUS.loading);
    await fetchUserEntity();
    setAuthStatus(STATUS.redirecting);
  };

  const linkFacebookAccount = async (
    payload: LinkFacebookAccountPayload,
    redirect = true,
  ) => {
    const account = await authApi.linkFacebookAccount(payload);
    if (redirect) setAuthStatus(STATUS.loading);
    await fetchUserEntity();
    if (redirect) setAuthStatus(STATUS.redirecting);
    return account;
  };

  const updateManagedCreatorRoleToCreator = async (userId: string) => {
    let jwtAccessToken: JWTAccessToken | undefined;

    const updatedRole = Array.from(
      new Set([...authApi.user.roles, USER_ROLES.creator]),
    ).filter((role) => role !== USER_ROLES.managed);

    jwtAccessToken = await authApi.updateUserRole({
      addRole: updatedRole,
      removeRole: [USER_ROLES.managed],
      userId,
    });

    setAuthStatus(STATUS.loading);

    await fetchUserEntity();

    if (jwtAccessToken) {
      // Update the jwtAccessToken in authApi and force redirect
      authApi.jwtAccessToken = jwtAccessToken;
    }
  };

  const storeTokens = ({ refreshToken, accessToken }: AuthTokens) => {
    if (Boolean(accessToken) && Boolean(refreshToken)) {
      authApi.jwtAccessToken = {
        accessToken: accessToken,
        refreshToken: refreshToken,
      } as JWTAccessToken;
    }
  };

  React.useEffect(() => {
    // Fetch user entity, store tokens and remove them from the url by redirecting to the current pathname
    const onUserIdChange = async () => {
      try {
        const hasTokens = hasTokensInUrl();

        if (
          hasTokens ||
          (authApi.user?.id && urlSearchParamsIncludes(['creatorId']))
        ) {
          const params = getUrlSearchParams();
          const {
            accessToken = '',
            creatorId = '',
            refreshToken = '',
            ...otherQueryParams
          } = mapURLSearchParamsToParams(params);
          const currentPathname = generatePath(window.location.pathname, {
            query: otherQueryParams,
          });

          if (hasTokens) {
            // It is always assigned even if it already had one assigned
            storeTokens({
              accessToken: accessToken,
              refreshToken: refreshToken,
            });
          }

          setAuthStatus(STATUS.loading);
          router.push(currentPathname);
        }
        await fetchUserEntity();
        setAuthStatus(STATUS.ready);
      } catch {
        setAuthStatus(STATUS.ready);
      }
    };

    onUserIdChange();
  }, [creator?.id]);

  React.useEffect(() => {
    const redirectUser = async () => {
      try {
        if (authStatus === STATUS.redirecting) {
          const {
            query: { redirectTo },
          } = router;
          setAuthStatus(STATUS.loading);

          await router.replace(
            (redirectTo as string | undefined) || getMainUrl(),
          );
          setAuthStatus(STATUS.ready);
        }
      } catch (error) {
        setAuthStatus(STATUS.ready);
      }
    };

    redirectUser();
  }, [router, getMainUrl, authStatus]);

  const providerValue = {
    acceptManagerInvite,
    getMainUrl,
    impersonate,
    linkFacebookAccount,
    logout,
    redirect,
    setSkipReconnect,
    skipReconnect,
    updateManagedCreatorRoleToCreator,
  };

  return (
    <>
      <AppLoader isVisible={authStatus !== STATUS.ready} />
      {authStatus === STATUS.ready && (
        <>
          <FeatureFlags />
          <AuthContext.Provider value={providerValue} {...props} />
        </>
      )}
    </>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};
